import React from 'react'
import styled from 'styled-components'
import { parseISO, format } from 'date-fns'
import { BridalPlanProps } from '../../../../BridalPlan.types'
import { getPrimaryColor } from '../../../../../../style/utils'
import { styleTheme } from '../../../../../../style/theme'

type ConditionsProps = {
  className?: string
  plan: BridalPlanProps | undefined
  primaryColor?: string
}

export const Conditions = (props: ConditionsProps) => {
  return <StyledComponent {...props} />
}

const applicablePeriodText = (
  applicableTypeText: 'ご来館' | 'ご成約' | '挙式',
  startDateText: string | null,
  endDateText: string | null,
) => {
  if (startDateText && endDateText) {
    return `${format(parseISO(startDateText), 'yyyy年MM月dd日')} 〜 ${format(parseISO(endDateText), 'yyyy年MM月dd日')}の間に${applicableTypeText}`
  }

  if (startDateText && !endDateText) {
    return `${format(parseISO(startDateText), 'yyyy年MM月dd日')}以降の${applicableTypeText}`
  }

  if (!startDateText && endDateText) {
    return `${format(parseISO(endDateText), 'yyyy年MM月dd日')}までに${applicableTypeText}`
  }

  return ''
}

const Component: React.FC<ConditionsProps> = ({ plan, className }) => {
  return (
    <>
      <div className={className}>
        <h2>プランの条件</h2>
        <table className="tb">
          <tbody>
            <tr>
              <th>適用人数</th>
              <td>
                {plan?.minNumberOfGuests && plan?.maxNumberOfGuests
                  ? `${plan?.minNumberOfGuests}名 〜 ${plan?.maxNumberOfGuests}名`
                  : '-'}
              </td>
            </tr>
            <tr>
              <th>適用期間</th>
              <td className="term-cell">
                {applicablePeriodText(
                  'ご来館',
                  plan?.applicableFirstVisitStartDate ?? null,
                  plan?.applicableFirstVisitEndDate ?? null,
                )}
                <br />
                {applicablePeriodText(
                  'ご成約',
                  plan?.applicableContractStartDate ?? null,
                  plan?.applicableContractEndDate ?? null,
                )}
                <br />
                {applicablePeriodText(
                  '挙式',
                  plan?.applicableWeddingStartDate ?? null,
                  plan?.applicableWeddingEndDate ?? null,
                )}
              </td>
            </tr>
            <tr>
              <th>1名増</th>
              <td>
                {['fixed_people', 'basic'].includes(plan?.displayType ?? '') && plan?.pricePerAdditionalGuest
                  ? `${(plan.pricePerAdditionalGuest - (plan.discountPerGuest || 0)).toLocaleString()}円 ${plan.discountPerGuest ? `(割引前${plan.pricePerAdditionalGuest.toLocaleString()}円)` : ''}`
                  : '-'}
              </td>
            </tr>
            <tr>
              <th>1名減</th>
              <td>
                {plan?.displayType === 'fixed_people' && plan?.pricePerReducedGuest
                  ? `- ${plan?.pricePerReducedGuest?.toLocaleString()}円`
                  : '-'}
              </td>
            </tr>
            <tr style={{ lineHeight: '16px' }}>
              <th>備考</th>
              <td colSpan={3} className="last-cell">
                <pre>{plan?.note}</pre>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  )
}

const StyledComponent = styled(Component)`
  > h2 {
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 18px;
    font-weight: bold;
    line-height: 27px;
    padding: 20px 0 0 0;
    color: ${getPrimaryColor};
    margin: 0 20px 20px 0;
    border-top: 1px dotted ${styleTheme.colors.gray.baseLight};
  }

  @media screen and (max-width: 520px) {
    > table.tb {
      th {
        width: 160px;
        font-size: 12px;
      }

      td {
        padding-left: 8px;
        width: 318px;
        font-size: 12px;
      }

      td.term-cell {
        line-height: 15px;
      }

      td.last-cell {
        border-bottom: 1px solid ${styleTheme.colors.gray.baseLight};
      }
    }
  }

  > table {
    color: ${styleTheme.colors.black};
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 14px;
    line-height: 23.8px;

    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    margin: 20px auto;

    > tbody {
      > tr {
        height: 47px;

        > th {
          background-color: ${styleTheme.colors.gray.light};
          width: 100px;
          font-weight: lighter;
          border: 1px solid ${styleTheme.colors.gray.baseLight};
        }

        > td {
          padding-left: 10px;
          padding-right: 8px;
          width: 398px;
          margin: 0;
          border: 1px solid ${styleTheme.colors.gray.baseLight};
        }

        > td.term-cell {
          line-height: 20px;
          padding-top: 10px;
          padding-bottom: 10px;
        }

        > td.last-cell {
          line-height: 20px;
          padding-top: 10px;
          padding-bottom: 10px;
        }
      }
    }
  }
`
